import type { TPalette, TParsedPalette, Theme } from '@apia/theme';
import { IApiaApiRequestConfig } from '../api/apiaApi/apiaApi';
import ConfigApi from '../api/ConfigApi';
import navigationLocker from '../classes/navigationLocker';
import {
  IApiaGlobalAdminData,
  TApiaSpecificListData,
} from '../components/apia/administration/types';
import { TEnvOptions } from '../components/apia/auth/forms/types';
import tabsController from '../components/apia/main/tabsController';
import { confirm } from '../components/common/dialogs/ConfirmProvider';
import ModalManager from '../components/common/dialogs/modals/iframeModal/ModalManager';
import {
  lockController,
  onScreenLockReleasedFirstTime,
} from '../components/common/dialogs/modals/ScreenLock';
import { TResponsiveTableColumn } from '../components/common/generic/responsiveTable/types';
import ActionButton from '../JSApi/ActionButton';
import Field from '../JSApi/constants/Field';
import Form from '../JSApi/constants/Form';
import IProperty from '../JSApi/constants/IProperty';
import { IApiaFunctions } from '../JSApi/types';
import { TApiaFilter, TComponentValidatorObjEvents, TMap } from '../types';
import { deleteCookie, getCookie } from '../utils/cookies';
import { globals, TId } from '@apia/util';
import { TApiaChartDefinition } from '../store/listSlice/types';

export interface IDefaults {
  closeTabConfirm: () => Promise<boolean>;
  context: TMap<unknown>;
  ADD_THOUSAND_SEPARATOR: boolean;
  AUTOCOMPLETE_OFF: boolean;
  CURRENT_STEP?: number;
  OBJ_NUM_REGEXP: RegExp;
  STEP_QTY: number;
  functionId?: number;
  isFavorite?: boolean;

  DEBUG_REACT: boolean;
  IS_EDIT_GRID_MODAL: boolean;
  MODAL_INDEX: number;

  // Reload Event
  LOAD_RELOAD_MAP_E: TMap<'load' | 'reload'>;
  LOAD_RELOAD_MAP_P: TMap<'load' | 'reload'>;

  isMonitor?: boolean;

  ActionButton: typeof ActionButton;
  ApiaFunctions: IApiaFunctions;
  ApiaApi: {
    config: (newConfig: Partial<IApiaApiRequestConfig<unknown>>) => void;
    reset: () => void;
    shout: () => void;
    theme: (theme: string) => void;
  };
  appliedTheme: Theme & { globals?: TMap<string> };
  applyBaseTheme: () => void;
  applyOtherTheme: (tag: string) => void;
  applyTheme: (theme?: Theme) => void;
  chart: TApiaChartDefinition;
  closeTabAction: () => unknown;
  ConfigApi: typeof ConfigApi;
  customTheme?: () => Theme;
  Field: typeof Field;
  Form: typeof Form;
  IProperty: typeof IProperty;
  isAdmEntity: boolean;
  isEntityViewMode: boolean;

  IS_EXTERNAL: boolean;
  IS_LINUX: boolean;
  isTesting: boolean;
  currentPalette: TParsedPalette;
  OptionButton: typeof ActionButton;

  customPalette?: TPalette;
  defaultPalette?: TPalette;
  debug: boolean;
  deleteCookie: typeof deleteCookie;
  getCookie: typeof getCookie;
  Globals: typeof globals;
  makeApiaUrl: () => string;
  SHOW_ENV_COMBO: boolean;
  SHOW_LINK_TO_REQUEST_PASSWORD_RESET: boolean;
  ENV_OPTIONS: TEnvOptions[];
  loginReact: boolean;
  LOGIN_OK: '0';
  LOGIN_ERROR: '1';
  LOGIN_CHANGE_PWD: '2';
  LOGIN_USER_EXPIRED: '3';
  LOGIN_USER_BLOCKED: '4';
  LOGIN_ERROR_CAPTCHA: '5';
  KEY_SIZE: number;
  IS_AUTHENTICATED: boolean;
  ITERATION_COUNT: number;

  LBL_DAYS: string[];

  CURRENT_ENVIRONMENT: number;
}

/**
 * El concepto de parser es usado en algunos componentes con la idea de
 * permitir modificar la visual de un área de la pantalla sin tocar React.
 *
 * Como ejemplo se puede ver window.parsers.validationErrors.
 */
export type TParser<ValueType = unknown> = (props: ValueType) => string;

export type TOnBeforeCloseResult = boolean | string;

export type TGetBodyController = () => ProxyHandler<{
  canRemoveTab: () => TOnBeforeCloseResult;
}>;

declare global {
  interface Window extends IDefaults {
    avoidLoadEvents: boolean;
    Confirm: typeof confirm;
    CONTEXT: string;
    customPalettes?: TPalette[];
    defaultPalette?: TPalette;
    getElementVariant: (element: HTMLElement) => void;
    getVariant: (variant: string) => {
      variant: string;
      'data-variant': string;
    };
    listData: TMap<TApiaSpecificListData>;
    globalAdminData: IApiaGlobalAdminData;
    lockController: typeof lockController;
    MAX_AMOUNT_TABS: number;
    navigationLocker: typeof navigationLocker;
    multipleListData?: TApiaSpecificListData[];
    onScreenLockReleasedFirstTime: typeof onScreenLockReleasedFirstTime;
    parsers: TMap<TParser>;
    printData: unknown;
    shouldCaptcha: boolean;
    specificListData: TApiaSpecificListData;
    tabsController: typeof tabsController;
    closeSession?: (dispatch?: boolean) => unknown;
    SERVER_MODE?: boolean;
    IS_EDITION_ALLOWED: boolean;
    [key: string]: string;
  }

  interface HTMLIFrameElement {
    classicCanRemoveTab: () => void;
    canRemoveTab: () => TOnBeforeCloseResult;
    erase: () => ProxyHandler<object>;
    fireEvent: () => ProxyHandler<object>;
    get: () => ProxyHandler<object>;
    getReactBodyController: TGetBodyController;
    handleDeadSession?: () => unknown;
    hasClass: () => boolean;
    modalManager: ModalManager;
    onBeforeClose: (callback: () => TOnBeforeCloseResult) => void;
    onFocusTab: () => unknown;
    set: () => unknown;
    tabId: TId;
  }

  const usersModal: {
    columns: TResponsiveTableColumn[];
    filters: TApiaFilter[];
    label: string;
    path: string;
  };
  const poolsModal: {
    columns: TResponsiveTableColumn[];
    filters: TApiaFilter[];
    label: string;
    path: string;
  };
  const documentsModal: {
    columns: TResponsiveTableColumn[];
    filters: TApiaFilter[];
    label: string;
    path: string;
  };
  const entitiesModal: {
    columns: TResponsiveTableColumn[];
    filters: TApiaFilter[];
    label: string;
    path: string;
  };
  const attributesModal: {
    columns: TResponsiveTableColumn[];
    filters: TApiaFilter[];
    label: string;
    path: string;
  };
  const queriesModal: {
    columns: TResponsiveTableColumn[];
    filters: TApiaFilter[];
    label: string;
    path: string;
  };
  const environmentsModal: {
    columns: TResponsiveTableColumn[];
    filters: TApiaFilter[];
    label: string;
    path: string;
  };
  const orgRolesModal: {
    columns: TResponsiveTableColumn[];
    filters: TApiaFilter[];
    label: string;
    path: string;
  };
  const addUserSecurityItemModal: {
    columns: TResponsiveTableColumn[];
    filters: TApiaFilter[];
    label: string;
    path: string;
  };
}

const DEFAULTS = {
  FRM_ACTION_NEXT: 'btnNext',
  FRM_ACTION_PREVIOUS: 'btnPrevious',
  FRM_ACTION_CONFIRM: 'btnConfirm',
  FRM_ACTION_SAVE: 'btnSave',
  FRM_ACTION_FREE: 'btnFree',
  FRM_ACTION_DELEGATE: 'btnDelegate',
  FRM_ACTION_CLOSE_TAB: 'btnCloseTab',

  // Field Events
  EVENT_FLD_ONCHANGE: 1,
  EVENT_FLD_ONCLICK: 4,
  EVENT_FLD_POPULATE: 5,
  EVENT_FLD_ONMODALRETURN: 37,
  EVENT_FLD_ONMODALCHANGE: 36,

  // Grid Events
  EVENT_FLD_GRID_ADD: 60,
  EVENT_FLD_GRID_DEL: 61,
  EVENT_FLD_GRID_SORT: 62,
  EVENT_FLD_GRID_COLUMN_SELECT: 65,

  // Print Events
  EVENT_FRM_ONBEFORE_PRINT: 63,
  EVENT_FRM_ONAFTER_PRINT: 64,

  // Form Events
  EVENT_FRM_ONLOAD: 'onLoad' as TComponentValidatorObjEvents,
  EVENT_FRM_ONSUBMIT: 'onSubmit' as TComponentValidatorObjEvents,
  EVENT_FRM_ONRELOAD: 'onReload' as TComponentValidatorObjEvents,

  // Messages
  ERR_MSG_FUNCTION: 'Error in function to be executed.',
};

const defaults = { ...DEFAULTS, ...Field, ...window };

export default defaults;
